import clsx from 'clsx';
import {
  type PointerEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { api, type Skill } from '../api';
import { useLanguage, useT } from '../i18n';
import { filterMentions, insertMention, type MentionItem, mentionQuery } from '../lib/mentions';
import { readAloud } from '../lib/readAloud';
import { dictationSupported, useDictation } from '../lib/useDictation';
import { isPhone, usePhone } from '../lib/usePhone';
import { usePushToTalk, usePushToTalkMode } from '../lib/usePushToTalk';
import { EMPTY_DRAFT, useDrafts } from '../stores/drafts';
import {
  DOCUMENT_ONLY_ACCEPT,
  isImageFilename,
  isSupportedAttachment,
  PROJECT_DOCUMENT_ACCEPT,
} from '../uploadAccept';
import { AiNotice } from './AiNotice';
import { CameraSheet, inAppCameraSupported } from './CameraSheet';
import { MentionMenu, mentionOptionId } from './MentionMenu';

const MAX_VISIBLE_LINES = 8;
const MIN_VISIBLE_LINES = 2;
/** On a phone the composer is one row that grows while typing. */
const PHONE_MIN_VISIBLE_LINES = 1;
const LINE_HEIGHT_PX = 23; // matches the medium text scale roughly

/**
 * Auto-growing composer textarea. Spec §5.1:
 *  - Enter sends, Shift+Enter inserts a newline; Cmd/Ctrl+Enter also sends
 *  - `Esc` cancels in-flight stream
 *  - max 8 lines visible then internal scroll
 */
export function Composer({
  busy,
  disabled,
  onSend,
  onStop,
  pendingAttachments = [],
  attachmentLimits,
  onAttachmentsChange,
  sqliteDatabases = [],
  skills = [],
  selectedSkillSlugs = [],
  onSkillChange,
  toolbar,
  draftKey,
  attachmentProjectName,
  light = false,
  dictationAvailable = false,
  readsAloud = false,
  mentions,
}: {
  /** What "@" can point at in this conversation, and what choosing one turns on. */
  mentions?: { items: MentionItem[]; onSelect: (item: MentionItem) => void } | undefined;
  /** The gateway has a speech-to-text model; without it there is no microphone button. */
  dictationAvailable?: boolean;
  /** A spoken question gets its answer read aloud (touch screen, available, switched on). */
  readsAloud?: boolean;
  draftKey?: string;
  attachmentProjectName?: string;
  light?: boolean;
  toolbar?: (
    onAttach: () => void,
    anchorRef: RefObject<HTMLFormElement | null>,
    onCamera: () => void,
    onPhotos: () => void,
    onDocuments: () => void,
  ) => ReactNode;
  busy: boolean;
  disabled?: boolean;
  /** `voice`: the text came (at least partly) from push to talk in this composer. */
  onSend: (text: string, attachments: File[], options: { voice: boolean }) => Promise<boolean>;
  onStop: () => void;
  pendingAttachments?: File[];
  /** Admin's attachment limits (bytes; null = none), shown as a fill meter. */
  attachmentLimits?: { maxFileBytes: number | null; maxTotalBytes: number | null } | undefined;
  onAttachmentsChange?: (files: File[]) => void;
  sqliteDatabases?: Array<{ id: string; filename: string }>;
  skills?: Skill[];
  selectedSkillSlugs?: string[];
  onSkillChange?: (slugs: string[], skills: Skill[]) => void;
}) {
  const [localValue, setLocalValue] = useState('');
  const draft = useDrafts((state) =>
    draftKey ? (state.entries[draftKey] ?? EMPTY_DRAFT) : EMPTY_DRAFT,
  );
  const value = draftKey ? draft.text : localValue;
  function setValue(next: string | ((value: string) => string)) {
    if (draftKey) {
      const current = useDrafts.getState().entries[draftKey]?.text ?? '';
      useDrafts
        .getState()
        .update(draftKey, { text: typeof next === 'function' ? next(current) : next });
    } else setLocalValue(next);
  }
  const submitting = useRef(false);
  const edits = useRef(0);
  const formRef = useRef<HTMLFormElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  // The phone's photo library: images only, without going through the camera.
  const photoInput = useRef<HTMLInputElement>(null);
  // Documents without images: on an iPhone it opens Files without its photo menu first.
  const documentInput = useRef<HTMLInputElement>(null);
  // On a phone "Take photo" opens the camera inside the app; elsewhere, or without camera
  // access, the phone's own camera app through the file input.
  const [camera, setCamera] = useState(false);
  const openCamera = () => {
    if (isPhone() && inAppCameraSupported()) setCamera(true);
    else cameraInput.current?.click();
  };
  const [activeSkillIndex, setActiveSkillIndex] = useState(0);
  const [caret, setCaret] = useState(0);
  const [activeMention, setActiveMention] = useState(0);
  const [dismissedMentionAt, setDismissedMentionAt] = useState<number | null>(null);
  const mentionListId = useId();
  const aiNoticeId = useId();
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const minLines = usePhone() ? PHONE_MIN_VISIBLE_LINES : MIN_VISIBLE_LINES;
  /** The message being written was (partly) spoken; cleared when sent or emptied. */
  const voiceTurn = useRef(false);
  const queuedVoice = useRef(false);
  const t = useT();
  const language = useLanguage();
  // A question spoken by holding the microphone leaves on release, like a voice message;
  // a click on the microphone (desktop, keyboard) only types it into the box.
  const sendSpoken = useRef(false);
  const dictation = useDictation({
    transcribe: async (file, signal) => (await api.transcribeAudio(file, language, signal)).text,
    onText: (text) => {
      edits.current += 1;
      voiceTurn.current = true;
      const full = value.trim() ? `${value.trimEnd()} ${text}` : text;
      if (sendSpoken.current && !busy && !disabled && !submitting.current) {
        sendSpoken.current = false;
        void send(full, pendingAttachments, full, true);
        return;
      }
      setValue(full);
      requestAnimationFrame(() => ref.current?.focus());
    },
  });
  const recording = dictation.state === 'recording';
  const cancelDictation = dictation.cancel;
  // Touch screens: hold the microphone to talk, release to convert. Desktop keeps click/stop.
  const pushToTalk = usePushToTalkMode();
  const ptt = usePushToTalk(dictation);
  const holdToTalk = pushToTalk && (ptt.holding || !recording);
  // Focus sits on the button that started the recording, so Escape is heard on the whole page.
  useEffect(() => {
    if (!recording) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      cancelDictation();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [recording, cancelDictation]);
  const canDictate = dictationAvailable && dictationSupported() && !busy && !disabled;
  const slashQuery = slashSkillQuery(value);
  const showSkillMenu = slashQuery !== null && !busy && !disabled && !!onSkillChange;
  const selectedSkillSet = useMemo(() => new Set(selectedSkillSlugs), [selectedSkillSlugs]);
  const filteredSkills = useMemo(() => {
    if (slashQuery === null) return [];
    const q = slashQuery.toLowerCase();
    return skills
      .filter((skill) =>
        [skill.name, skill.slug, skill.description, skill.domain, ...skill.tags]
          .join(' ')
          .toLowerCase()
          .includes(q),
      )
      .sort(
        (a, b) =>
          skillSortScore(b, q, selectedSkillSet) - skillSortScore(a, q, selectedSkillSet) ||
          a.name.localeCompare(b.name),
      )
      .slice(0, 8);
  }, [selectedSkillSet, skills, slashQuery]);
  const groupedSkills = useMemo(() => groupSkillsByDomain(filteredSkills), [filteredSkills]);
  const mentionAt = mentions && !showSkillMenu ? mentionQuery(value, caret) : null;
  const mentionItems = useMemo(
    () => (mentionAt && mentions ? filterMentions(mentions.items, mentionAt.query) : []),
    [mentionAt?.query, mentions],
  );
  const showMentionMenu =
    mentionAt !== null &&
    mentionAt.start !== dismissedMentionAt &&
    !busy &&
    !disabled &&
    (mentions?.items.length ?? 0) > 0;

  useEffect(() => {
    setActiveMention(0);
  }, [mentionAt?.query]);

  function pickMention(item: MentionItem) {
    if (!mentionAt || !mentions) return;
    const next = insertMention(value, mentionAt, item.label);
    edits.current += 1;
    setValue(next.value);
    setCaret(next.caret);
    mentions.onSelect(item);
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(next.caret, next.caret);
      setCaret(next.caret);
    });
  }

  useEffect(() => {
    setActiveSkillIndex(0);
  }, [slashQuery]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    const max = LINE_HEIGHT_PX * MAX_VISIBLE_LINES + 16;
    const min = LINE_HEIGHT_PX * minLines + 16;
    el.style.height = `${Math.min(Math.max(el.scrollHeight, min), max)}px`;
  }, [value, minLines]);

  useEffect(() => {
    if (activeSkillIndex >= filteredSkills.length) setActiveSkillIndex(0);
  }, [activeSkillIndex, filteredSkills.length]);

  /** Puts an unsent message back, unless its draft was handed off (moved/removed) in the meantime. */
  const [queued, setQueued] = useState<string | null>(null);
  const [rejected, setRejected] = useState<string | null>(null);

  function restoreDraft(text: string, submittedAtEdit: number) {
    if (draftKey && !(draftKey in useDrafts.getState().entries)) return;
    setValue((current) => (current === '' && edits.current === submittedAtEdit ? text : current));
  }

  async function submit() {
    const text = value.trim();
    if ((!text && pendingAttachments.length === 0) || disabled || submitting.current) return;
    const voice = voiceTurn.current;
    // Sending stops what is read aloud; a spoken question unlocks audio during this tap (iOS).
    readAloud.stop();
    if (voice && readsAloud) readAloud.unlock();
    if (busy) {
      // An answer is still running: the message waits in line and leaves when it is done.
      if (!text) return;
      setQueued((current) => (current ? `${current}\n\n${text}` : text));
      queuedVoice.current ||= voice;
      voiceTurn.current = false;
      setValue('');
      return;
    }
    await send(text, pendingAttachments, value, voice);
  }

  async function send(text: string, files: File[], restoreText: string, voice: boolean) {
    submitting.current = true;
    const submittedAtEdit = edits.current;
    voiceTurn.current = false;
    setValue('');
    try {
      const sent = await onSend(text, files, { voice });
      if (sent === false) {
        restoreDraft(restoreText, submittedAtEdit);
        return;
      }
      onAttachmentsChange?.([]);
      requestAnimationFrame(() => ref.current?.focus());
    } catch {
      restoreDraft(restoreText, submittedAtEdit);
    } finally {
      submitting.current = false;
    }
  }

  // The queued message leaves as soon as the running answer is done.
  useEffect(() => {
    if (busy || disabled || !queued || submitting.current) return;
    const text = queued;
    const voice = queuedVoice.current;
    setQueued(null);
    queuedVoice.current = false;
    void send(text, pendingAttachments, text, voice);
  }, [busy, disabled, queued]);

  /** Adds what can be attached; returns the new list, or null when nothing can be added now. */
  function addFiles(files: File[]): File[] | null {
    if (busy || disabled || submitting.current || !onAttachmentsChange) return null;
    const next = [...pendingAttachments];
    // Say at once what cannot be attached, instead of failing after sending.
    const unsupported = files.filter((file) => !isSupportedAttachment(file.name));
    const maxFile = attachmentLimits?.maxFileBytes ?? null;
    const tooLarge = files.filter(
      (file) => isSupportedAttachment(file.name) && maxFile !== null && file.size > maxFile,
    );
    const problems = [
      ...(unsupported.length > 0
        ? [t('attachmentUnsupported').replace('{files}', unsupported.map((f) => f.name).join(', '))]
        : []),
      ...tooLarge.map((file) =>
        t('attachmentFileTooLarge')
          .replace('{filename}', file.name)
          .replace('{limit}', formatAttachmentSize(maxFile ?? 0)),
      ),
    ];
    let over = 0;
    for (const file of files) {
      if (unsupported.includes(file) || tooLarge.includes(file)) continue;
      if (next.length >= MAX_FILES_PER_MESSAGE) {
        over += 1;
        continue;
      }
      if (
        !next.some(
          (item) =>
            item.name === file.name &&
            item.size === file.size &&
            item.lastModified === file.lastModified,
        )
      )
        next.push(file);
    }
    if (over > 0) problems.push(t('attachmentTooManyFiles').replace('{count}', String(over)));
    setRejected(problems.length > 0 ? problems.join(' ') : null);
    onAttachmentsChange(next);
    return next;
  }

  /** Photos from the in-app camera: sent at once with their question, or only added. */
  function cameraDone(files: File[], question: string) {
    setCamera(false);
    const attached = addFiles(files);
    if (!question) return;
    // A draft already in the box stays: the question joins it instead of leaving alone.
    if (attached && !value.trim()) void send(question, attached, question, false);
    else setValue((current) => (current.trim() ? `${current.trim()}\n\n${question}` : question));
  }

  function selectSkill(skill: Skill | null) {
    if (!onSkillChange) return;
    if (!skill) {
      onSkillChange([], []);
    } else {
      const key = skillKey(skill);
      const isSelected = selectedSkillSet.has(key) || selectedSkillSet.has(skill.slug);
      const nextKeys = isSelected
        ? selectedSkillSlugs.filter((slug) => slug !== key && slug !== skill.slug)
        : [...selectedSkillSlugs, key];
      const nextKeySet = new Set(nextKeys);
      const nextSkills = skills.filter(
        (candidate) => nextKeySet.has(skillKey(candidate)) || nextKeySet.has(candidate.slug),
      );
      onSkillChange(nextKeys, nextSkills);
    }
    setValue((current) => removeSlashSkillCommand(current));
    requestAnimationFrame(() => ref.current?.focus());
  }

  return (
    <>
      <form
        ref={formRef}
        data-tour="composer"
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes('Files')) e.preventDefault();
        }}
        onDrop={(e) => {
          if (!e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          addFiles(Array.from(e.dataTransfer.files));
        }}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="nv-composer nv-composer-live composer-form"
      >
        {pendingAttachments.length > 0 && attachmentProjectName && (
          // Only where it differs from what people expect: the file goes into the project.
          <p className="text-12 text-text-secondary">
            {`${t('attachmentInProject')}: ${attachmentProjectName}`}
          </p>
        )}
        <AttachmentMeters files={pendingAttachments} limits={attachmentLimits} />
        {rejected && (
          <p className="text-12 text-danger" role="alert">
            {rejected}
          </p>
        )}
        {dictation.state !== 'idle' && (
          <p className="text-12 text-text-secondary" role="status">
            {dictation.state !== 'recording'
              ? t('transcribing')
              : ptt.holding
                ? t(ptt.cancelArmed ? 'dictateHoldCancel' : 'dictateHolding')
                : t('dictating')}
          </p>
        )}
        {dictation.state === 'idle' && ptt.hint && (
          <p className="text-12 text-text-secondary" role="status">
            {t(ptt.hint === 'holdAgain' ? 'dictateHoldAgain' : 'dictateHoldHint')}
          </p>
        )}
        {dictation.error && (
          <p className="text-12 text-danger" role="alert">
            {dictation.error === 'denied' ? t('dictationDenied') : t('dictationFailed')}
          </p>
        )}
        {queued && (
          <div className="nv-queued" role="status">
            <span className="min-w-0 flex-1 truncate">
              <b>{t('queuedMessage')}</b> {queued}
            </span>
            <button
              type="button"
              className="btn-ghost shrink-0"
              onClick={() => {
                setValue((current) => (current ? `${queued}\n\n${current}` : queued));
                voiceTurn.current ||= queuedVoice.current;
                queuedVoice.current = false;
                setQueued(null);
                ref.current?.focus();
              }}
            >
              {t('queuedCancel')}
            </button>
          </div>
        )}
        {showMentionMenu && (
          <MentionMenu
            id={mentionListId}
            items={mentionItems}
            activeIndex={activeMention}
            onHover={setActiveMention}
            onPick={pickMention}
          />
        )}
        {showSkillMenu && (
          <div
            className="absolute right-3 bottom-[calc(100%+8px)] left-3 z-20 overflow-y-auto rounded-lg border border-border-subtle bg-bg-panel [box-shadow:var(--shadow-popover)]"
            style={{ maxHeight: 'min(60dvh, 28rem)' }}
          >
            <button
              type="button"
              onClick={() => selectSkill(null)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-13 hover:bg-bg-hover"
            >
              <span
                className={clsx(
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                  selectedSkillSlugs.length === 0
                    ? 'border-[var(--primary-bg,var(--accent))] bg-[var(--primary-bg,var(--accent))] text-[var(--primary-fg,#fff)]'
                    : 'border-border-strong',
                )}
                aria-hidden
              >
                {selectedSkillSlugs.length === 0 ? '✓' : ''}
              </span>
              <span className="font-medium text-text-primary">{t('noSkill')}</span>
            </button>
            {filteredSkills.length === 0 ? (
              <div className="px-3 py-2 text-12 text-text-tertiary">{t('noSkills')}</div>
            ) : (
              groupedSkills.map((group) => (
                <div key={group.domain}>
                  <div className="px-3 pt-2 pb-1 text-10 font-semibold text-text-tertiary uppercase tracking-[0.08em]">
                    {group.domain}
                  </div>
                  {group.skills.map((skill) => {
                    const index = filteredSkills.indexOf(skill);
                    const key = skillKey(skill);
                    const on = selectedSkillSet.has(key) || selectedSkillSet.has(skill.slug);
                    return (
                      <button
                        key={key}
                        type="button"
                        onMouseEnter={() => setActiveSkillIndex(index)}
                        onClick={() => selectSkill(skill)}
                        className={clsx(
                          'flex w-full items-start gap-2 px-3 py-2 text-left text-13',
                          index === activeSkillIndex ? 'bg-bg-hover' : 'hover:bg-bg-hover',
                        )}
                      >
                        <span
                          className={clsx(
                            'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                            on
                              ? 'border-[var(--primary-bg,var(--accent))] bg-[var(--primary-bg,var(--accent))] text-[var(--primary-fg,#fff)]'
                              : 'border-border-strong',
                          )}
                          aria-hidden
                        >
                          {on ? '✓' : ''}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium text-text-primary">
                            {skill.name}
                          </span>
                          <span className="block truncate text-11 text-text-tertiary">
                            {skill.effort}
                          </span>
                          {skill.description.length > 0 && (
                            <span className="mt-0.5 block line-clamp-2 text-12 text-text-secondary">
                              {skill.description}
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        )}
        {pendingAttachments.some((file) => isImageFilename(file.name)) && (
          <ul className="nv-athumbs" aria-label={t('attachments')}>
            {pendingAttachments
              .filter((file) => isImageFilename(file.name))
              .map((file) => (
                <li
                  key={`${file.name}-${file.size}-${file.lastModified}`}
                  className="nv-athumb"
                  title={`${file.name} · ${formatAttachmentSize(file.size)}`}
                >
                  <AttachmentThumbnail file={file} />
                  <button
                    type="button"
                    className="nv-athumb-x"
                    disabled={busy || disabled}
                    onClick={() =>
                      onAttachmentsChange?.(pendingAttachments.filter((other) => other !== file))
                    }
                    aria-label={`${t('removeAttachment')}: ${file.name}`}
                  >
                    ×
                  </button>
                </li>
              ))}
          </ul>
        )}
        {pendingAttachments.some((file) => !isImageFilename(file.name)) && (
          <ul
            className="max-h-28 w-full shrink-0 overflow-y-auto overflow-x-hidden divide-y divide-border-subtle text-12 text-text-secondary"
            aria-label={t('attachments')}
          >
            {pendingAttachments
              .filter((file) => !isImageFilename(file.name))
              .map((file) => (
                <li
                  key={`${file.name}-${file.size}-${file.lastModified}`}
                  className="flex min-w-0 items-center gap-2 py-1"
                >
                  <span
                    className="min-w-0 flex-1 truncate"
                    {...(file.name.length > 40 ? { title: file.name } : {})}
                  >
                    {file.name}
                  </span>
                  <span className="shrink-0 text-text-tertiary">
                    {formatAttachmentSize(file.size)}
                  </span>
                  <button
                    type="button"
                    className="btn-ghost shrink-0"
                    disabled={busy || disabled}
                    onClick={() =>
                      onAttachmentsChange?.(pendingAttachments.filter((other) => other !== file))
                    }
                    aria-label={`${t('removeAttachment')}: ${file.name}`}
                    title={`${t('removeAttachment')}: ${file.name}`}
                  >
                    ×
                  </button>
                </li>
              ))}
          </ul>
        )}
        {sqliteDatabases.length > 0 && pendingAttachments.length === 0 && (
          <div className="flex min-w-0 items-center gap-2 text-12 text-text-secondary">
            <DatabaseIcon />
            <span className="truncate" title={sqliteDatabases.map((db) => db.filename).join(', ')}>
              {t('sqliteToolAvailable')}: {sqliteDatabases.map((db) => db.filename).join(', ')}
            </span>
          </div>
        )}
        <input
          ref={fileInput}
          type="file"
          aria-label={t('attachFile')}
          multiple
          className="hidden"
          disabled={busy || disabled}
          accept={PROJECT_DOCUMENT_ACCEPT}
          onChange={(e) => {
            const files = Array.from(e.currentTarget.files ?? []);
            e.currentTarget.value = '';
            addFiles(files);
          }}
        />
        {camera && (
          <CameraSheet
            onDone={cameraDone}
            onClose={() => setCamera(false)}
            onFallback={() => {
              setCamera(false);
              cameraInput.current?.click();
            }}
          />
        )}
        <input
          ref={documentInput}
          type="file"
          aria-label={t('attachFile')}
          multiple
          className="hidden"
          disabled={busy || disabled}
          accept={DOCUMENT_ONLY_ACCEPT}
          onChange={(e) => {
            const files = Array.from(e.currentTarget.files ?? []);
            e.currentTarget.value = '';
            addFiles(files);
          }}
        />
        <input
          ref={photoInput}
          type="file"
          aria-label={t('choosePhotos')}
          multiple
          className="hidden"
          disabled={busy || disabled}
          accept="image/*"
          onChange={(e) => {
            const files = Array.from(e.currentTarget.files ?? []);
            e.currentTarget.value = '';
            addFiles(files);
          }}
        />
        <input
          ref={cameraInput}
          type="file"
          aria-label={t('takePhoto')}
          className="hidden"
          disabled={busy || disabled}
          accept="image/*"
          capture="environment"
          onChange={(e) => {
            const files = Array.from(e.currentTarget.files ?? []);
            e.currentTarget.value = '';
            addFiles(files);
          }}
        />
        <textarea
          ref={ref}
          data-composer=""
          value={value}
          onChange={(e) => {
            edits.current += 1;
            if (!e.target.value.trim()) voiceTurn.current = false;
            setValue(e.target.value);
            setCaret(e.target.selectionStart ?? e.target.value.length);
          }}
          onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
          aria-describedby={aiNoticeId}
          aria-autocomplete={mentions ? 'list' : undefined}
          aria-controls={showMentionMenu && mentionItems.length > 0 ? mentionListId : undefined}
          aria-activedescendant={
            showMentionMenu && mentionItems.length > 0
              ? mentionOptionId(mentionListId, activeMention)
              : undefined
          }
          onPaste={(e) => {
            const files = Array.from(e.clipboardData.files).filter((file) =>
              file.type.startsWith('image/'),
            );
            if (files.length === 0) return;
            e.preventDefault();
            addFiles(files);
          }}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing) return;
            if (showMentionMenu) {
              const count = mentionItems.length;
              if (e.key === 'ArrowDown' && count > 0) {
                e.preventDefault();
                setActiveMention((i) => (i + 1) % count);
                return;
              }
              if (e.key === 'ArrowUp' && count > 0) {
                e.preventDefault();
                setActiveMention((i) => (i - 1 + count) % count);
                return;
              }
              if ((e.key === 'Enter' || e.key === 'Tab') && count > 0 && !e.shiftKey) {
                e.preventDefault();
                const item = mentionItems[activeMention] ?? mentionItems[0];
                if (item) pickMention(item);
                return;
              }
              if (e.key === 'Escape' && mentionAt) {
                e.preventDefault();
                setDismissedMentionAt(mentionAt.start);
                return;
              }
            }
            if (showSkillMenu && filteredSkills.length > 0) {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveSkillIndex((i) => (i + 1) % filteredSkills.length);
                return;
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveSkillIndex((i) => (i - 1 + filteredSkills.length) % filteredSkills.length);
                return;
              }
              if (e.key === 'Enter') {
                e.preventDefault();
                selectSkill(filteredSkills[activeSkillIndex] ?? filteredSkills[0] ?? null);
                return;
              }
            }
            if (e.key === 'Enter' && (!e.shiftKey || e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void submit();
              return;
            }
            if (e.key === 'Escape' && dictation.state !== 'idle') {
              e.preventDefault();
              dictation.cancel();
              return;
            }
            if (e.key === 'Escape' && busy) {
              e.preventDefault();
              onStop();
              return;
            }
            if (e.key === 'Escape' && showSkillMenu) {
              e.preventDefault();
              setValue((current) => removeSlashSkillCommand(current));
            }
          }}
          rows={minLines}
          // Enter sends (see onKeyDown), so the on-screen keyboard shows a Send key.
          enterKeyHint="send"
          placeholder={busy ? t('generating') : t('typeMessage')}
          disabled={disabled}
          className="disabled:cursor-not-allowed disabled:opacity-50"
          aria-label={t('message')}
        />
        <div className="nv-ctools min-w-0">
          {toolbar ? (
            toolbar(
              () => fileInput.current?.click(),
              formRef,
              openCamera,
              () => photoInput.current?.click(),
              () => documentInput.current?.click(),
            )
          ) : (
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              className="nv-icon-btn nv-ct-add"
              data-tour="context"
              disabled={busy || disabled}
              aria-label={t('attachFile')}
              title={t('attachFile')}
            >
              <PlusIcon />
            </button>
          )}
          {canDictate && (dictation.state !== 'recording' || ptt.holding) && (
            // The slot keeps the toolbar's layout while the held button grows over it.
            <span className="nv-ct-mic-slot">
              <button
                type="button"
                // While recording, the round Send button becomes the stop button instead. On touch
                // screens the button is held to talk; a keyboard still toggles (detail 0 = keyboard).
                onClick={(event) => {
                  if (holdToTalk && event.detail !== 0) return;
                  sendSpoken.current = false;
                  readAloud.stop();
                  void dictation.start();
                }}
                {...(holdToTalk
                  ? {
                      ...ptt.handlers,
                      // Starting a new recording stops what is being read aloud.
                      onPointerDown: (event: PointerEvent<HTMLButtonElement>) => {
                        sendSpoken.current = true;
                        readAloud.stop();
                        // The answer to a spoken question is read aloud: sound unlocks in this touch.
                        if (readsAloud) readAloud.unlock();
                        growToToolbar(event.currentTarget);
                        ptt.handlers.onPointerDown(event);
                      },
                    }
                  : {})}
                className={`nv-icon-btn nv-ct-mic${pushToTalk ? ' nv-ct-mic-ptt' : ''}${
                  ptt.holding ? ' nv-ct-mic-hold' : ''
                }${ptt.holding && recording ? ' nv-ct-mic-live' : ''}${ptt.cancelArmed ? ' nv-ct-mic-cancel' : ''}`}
                data-tour="dictate"
                disabled={dictation.state === 'transcribing'}
                aria-label={pushToTalk ? t('dictateHold') : t('dictate')}
                aria-pressed={pushToTalk ? ptt.holding : undefined}
                title={pushToTalk ? t('dictateHold') : t('dictate')}
              >
                <MicIcon />
                {ptt.holding && (
                  <span className="nv-ct-mic-label">
                    {t(ptt.cancelArmed ? 'dictateHoldCancel' : 'dictateHoldLabel')}
                  </span>
                )}
              </button>
            </span>
          )}
          {dictation.state === 'recording' && !ptt.holding ? (
            <button
              type="button"
              onClick={() => dictation.stop()}
              className="nv-send nv-send-icon"
              aria-label={t('dictateStop')}
              title={`${t('dictateStop')} (Esc)`}
            >
              <StopIcon />
            </button>
          ) : busy ? (
            <button
              type="button"
              onClick={(event) => {
                // Cancellation can immediately switch this control back to Submit.
                event.preventDefault();
                onStop();
              }}
              className="nv-send nv-send-icon"
              aria-label={t('stopGenerating')}
              title={`${t('stop')} (Esc)`}
            >
              <StopIcon />
            </button>
          ) : (
            <button
              type="submit"
              // Keep the focus (and the keyboard) in the box until the tap lands: losing it first
              // closes the keyboard, the screen moves and the tap misses Send.
              onPointerDown={(event) => event.preventDefault()}
              onMouseDown={(event) => event.preventDefault()}
              disabled={(value.trim().length === 0 && pendingAttachments.length === 0) || disabled}
              className="nv-send nv-send-icon"
              aria-label={t('send')}
              title={`${t('send')} (Enter)`}
            >
              <ArrowUpIcon />
            </button>
          )}
        </div>
        {!light && <p className="sr-only">{t('sendHint')}</p>}
      </form>
      <AiNotice id={aiNoticeId} />
    </>
  );
}

export const MAX_FILES_PER_MESSAGE = 20;

/**
 * How much of a limit the attachments fill: the files against 20 per message, and the total
 * size against Admin's per-message limit when one is set. Amber from 90%, red above the limit.
 */
/** Share of a limit from which the attachment meters show. */
const METER_FROM = 0.9;

function AttachmentMeters({
  files,
  limits,
}: {
  files: File[];
  limits?: { maxTotalBytes: number | null } | undefined;
}) {
  const t = useT();
  const count = files.length;
  const total = files.reduce((sum, file) => sum + file.size, 0);
  const maxTotal = limits?.maxTotalBytes ?? null;
  // Quiet until a limit is near: most messages carry a file or two.
  const near =
    count >= MAX_FILES_PER_MESSAGE * METER_FROM ||
    (maxTotal !== null && total >= maxTotal * METER_FROM);
  if (count === 0 || !near) return null;
  return (
    <div className="nv-meters">
      <FillMeter
        label={t('attachmentImagesMeter')}
        value={count}
        max={MAX_FILES_PER_MESSAGE}
        text={`${count} / ${MAX_FILES_PER_MESSAGE}`}
      />
      {maxTotal !== null && (
        <FillMeter
          label={t('attachmentSizeMeter')}
          value={total}
          max={maxTotal}
          text={`${formatAttachmentSize(total)} / ${formatAttachmentSize(maxTotal)}`}
        />
      )}
    </div>
  );
}

function FillMeter({
  label,
  value,
  max,
  text,
}: {
  label: string;
  value: number;
  max: number;
  text: string;
}) {
  const ratio = max > 0 ? value / max : 0;
  return (
    <div className="nv-meter-row">
      <span className="nv-meter-label">{label}</span>
      <meter
        className={clsx('nv-meter', ratio > 1 ? 'nv-meter-over' : ratio >= 0.9 && 'nv-meter-near')}
        aria-label={label}
        min={0}
        max={max}
        value={Math.min(value, max)}
        aria-valuetext={text}
      />
      <span className="nv-meter-text tabular-nums">{text}</span>
    </div>
  );
}

/** Small preview of a picked image; the object URL lives as long as the thumbnail does. */
function AttachmentThumbnail({ file }: { file: File }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (typeof URL.createObjectURL !== 'function') return;
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return url ? (
    <img src={url} alt={file.name} className="nv-athumb-img" />
  ) : (
    <span className="nv-athumb-img" />
  );
}

export function formatAttachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function PlusIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-[18px] w-[18px]"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-[18px] w-[18px]"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 19V5M6 11l6-6 6 6" />
    </svg>
  );
}

/**
 * A held microphone button grows into a wide bar over the whole toolbar. Its left edge and width
 * are measured here, because the slot it sits in is only as big as the resting button.
 */
function growToToolbar(button: HTMLElement): void {
  // On a phone the toolbar's parts sit in the composer row itself (display: contents), so the
  // held button grows over that row instead.
  const tools = button.closest('.nv-ctools')?.getBoundingClientRect();
  const toolbar = tools?.width ? tools : button.closest('form')?.getBoundingClientRect();
  const slot = button.parentElement?.getBoundingClientRect();
  if (!toolbar || !slot) return;
  button.style.setProperty('--hold-left', `${toolbar.left - slot.left}px`);
  button.style.setProperty('--hold-width', `${toolbar.width}px`);
}

function MicIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3" />
    </svg>
  );
}

function DatabaseIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <ellipse cx="12" cy="5" rx="8" ry="3" />
      <path d="M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5" />
      <path d="M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="7" y="7" width="10" height="10" rx="1.5" />
    </svg>
  );
}

function skillKey(skill: Pick<Skill, 'teamId' | 'slug'>): string {
  return `${skill.teamId}/${skill.slug}`;
}

function skillSortScore(skill: Skill, query: string, selected: Set<string>): number {
  let score = selected.has(skillKey(skill)) || selected.has(skill.slug) ? 100 : 0;
  const name = skill.name.toLowerCase();
  const slug = skill.slug.toLowerCase();
  const domain = skill.domain.toLowerCase();
  if (name.startsWith(query)) score += 30;
  else if (name.includes(query)) score += 20;
  if (slug.includes(query)) score += 12;
  if (domain.includes(query)) score += 8;
  if (skill.tags.some((tag) => tag.toLowerCase().includes(query))) score += 6;
  if (skill.description.toLowerCase().includes(query)) score += 4;
  return score;
}

function groupSkillsByDomain(skills: Skill[]): Array<{ domain: string; skills: Skill[] }> {
  const groups = new Map<string, Skill[]>();
  for (const skill of skills) {
    const domain = skill.domain || 'Skills';
    groups.set(domain, [...(groups.get(domain) ?? []), skill]);
  }
  return Array.from(groups, ([domain, groupSkills]) => ({ domain, skills: groupSkills }));
}

function slashSkillQuery(value: string): string | null {
  const match = /^\/([^\s]*)$/.exec(value.trimStart());
  return match?.[1] ?? null;
}

function removeSlashSkillCommand(value: string): string {
  return value.replace(/^\s*\/[^\s]*\s*/, '');
}
